import { UIIcon } from '../../config/icons';
import { c_cities } from '../mdl/c-city';
import { c_event_tags, cEventTagName } from '../mdl/c-event-tag';
import { c_producers } from '../mdl/c-producer';
import { c_venues } from '../mdl/c-venue';
import { UIButton } from '../ui/component/button';
import { UIDatePicker, UIDateRangeInput } from '../ui/component/date';
import { UIFilterPopup } from '../ui/component/filter-popup';
import { UISearchInput } from '../ui/component/input';
import { UISelect } from '../ui/component/select';
import { UIMultiToggles, UITickCheckbox, UIToggleButton } from '../ui/component/toggle';
import { $el } from '../ui/elements';
import { UIMenu, uiShowMenu } from '../ui/menu';
import { uiHidePopup, uiShowPopup } from '../ui/popup';
import { UIRelPos, UIValue } from '../ui/types';
import { UIView, UIViewAddSpec, UIViewSpec } from '../ui/view';
import { $on } from '../y/events';
import { YInput } from '../y/form';
import { $i18n } from '../y/i18n';
import { YId } from '../y/types';
import { yResolve } from '../y/util';

export interface ABFilter {
    key: string;
    title: string;
    value_text: string;
    value: any;
}

export interface ABFilterBarOptions {
    on_search?: () => void;
    on_sort?: () => void;
    on_filter?: () => void;
    on_refresh?: () => void;
    sort_options?: UIValue<string>[];
    left_content?: UIViewAddSpec;
    right_content?: UIViewAddSpec;
    no_header?: boolean;
}

export class ABFilterBar extends UIView {
    search_input?: UISearchInput;
    sort_input?: UISelect<string>;
    filters: TLNewFilterManager;

    constructor(options: ABFilterBarOptions) {
        super('div', 'tl-new-filter-bar b');
        const filters = this.filters = new TLNewFilterManager;

        if(options.on_search) {
            this.ui_add($el.DIV('tl-new-filter-group m-search', [
                !options.no_header && $el.DIV('r-title', $i18n.pr.filter_search),
                this.search_input = new UISearchInput({ kind: 'k-subtle' })
            ]));
            $on(this.search_input, 'value', () => { options.on_search!(); })
        }

        if(options.left_content) {
            this.ui_add($el.DIV('ab-filter-right', options.left_content));
        }

        if(options.on_filter) {
            filters.on_filters = options.on_filter;
            this.ui_add($el.DIV('tl-new-filter-group m-filter', [
                !options.no_header && $el.DIV('r-title', $i18n.pr.filter_filter),
                filters.container
            ]));
        }

        if(options.on_sort && options.sort_options?.length) {
            this.ui_add($el.DIV('tl-new-filter-group m-sort', [
                !options.no_header && $el.DIV('r-title', $i18n.pr.filter_sort),
                this.sort_input = new UISelect(options.sort_options, { kind: 'k-subtle' })
            ]));
            this.sort_input.value = options.sort_options[0].key;
            $on(this.sort_input, 'value', () => { options.on_sort!(); })
        }

        if(options.on_refresh) {
            const refresh = new UIButton({ kind: 'k-filter', icon: UIIcon.ROTATE, on_click: options.on_refresh });
            options.right_content = [options.right_content, refresh];
        }

        if(options.right_content) {
            this.ui_add([
                $el.S(),
                $el.DIV('ab-filter-right', options.right_content)
            ]);
        }
    }
}

interface TLNewFilterSpec {
    key: string;
    title: string;
    prefix?: string;
    no_search?: boolean;
    values?: () => UIValue<any>[];
    type?: 'date' | 'single';
}

export class TLNewFilterManager {
    container: HTMLDivElement;
    on_filters?: () => void;
    filter_keys: string[] = [];
    value: Record<string, any> = {};
    filters: ABFilter[] = [];
    private _inputs: Record<string, UIView & YInput<any>> = {};
    private _specs: TLNewFilterSpec[] = [];
    private _chip_container: HTMLDivElement;

    constructor() {
        this.container = $el.DIV('r-content', [
            this._chip_container = $el.DIV('ab-filter-chips')
        ]);
        this._chip_container.ui_hide();
    }

    addFilterButton() {
        this.container.ui_add(new UIButton({
            kind: 'k-text', title: 'FILTER', on_click: b => {
                this.showMenu(b);
            }
        }));
    }

    addWidgets(widgets: UIViewAddSpec) {
        this.container.ui_add(widgets);
    }

    addSelect(key: string, title: string, entries: UIValue<string | undefined>[], all?: string) {
        if(all)
            entries.unshift({ key: undefined, name: all });
        const select = new UISelect<string | undefined>(entries, { placeholder: title, kind: 'k-subtle', searchable: entries.length > 10 });
        this.container.ui_add(select);
        this.filter_keys.push(key);
        this._inputs[key] = select;
        if(all)
            select.value = undefined;
        $on(select, 'value', () => {
            this.value[key] = select.value;
            this.on_filters?.();
        });
    }

    addCheckbox(key: string, title: string, value?: boolean) {
        const checkbox = new UITickCheckbox({ title });
        if(value) {
            this.value[key] = true;
            checkbox.value = true;
        }
        this.container.ui_add(checkbox);
        this.filter_keys.push(key);
        this._inputs[key] = checkbox;
        $on(checkbox, 'value', () => {
            this.value[key] = checkbox.value;
            this.on_filters?.();
        });
    }

    addEventPills() {
        const values: UIValue<string>[] = [{ key: 'live', name: $i18n.pr.filter_event_live }, { key: 'unpublished', name: $i18n.pr.filter_event_unpublished }, { key: 'past', name: $i18n.pr.filter_event_previous }];//, {key: 'imported', name: $i18n.pr.filter_event_imported}];
        const toggles = new UIMultiToggles(values, c => new UIToggleButton({ key: c.key, title: c.name, classes: 'ui-tint-fg' }), { classes: 'ui-toggle-buttons tl-producer-toggles' });
        toggles.value = ['live', 'unpublished', 'past'];//, 'imported'];
        this.container.ui_add(toggles);
        this.filter_keys.push('event_status');
        this._inputs['event_status'] = toggles;
        $on(toggles, 'value', () => {
            this.value['event_status'] = toggles.value;
            this.on_filters?.();
        });
    }

    addDateRange(key: string, title: string) {
        const input = new UIDateRangeInput({ kind: 'k-subtle', placeholder: title });
        this.container.ui_add(input);
        this.filter_keys.push(key);
        this._inputs[key] = input;
        $on(input, 'value', () => {
            this.value[key] = input.value;
            this.on_filters?.();
        });
    }

    addFullStatus() {
        this._specs.push({
            key: 'account_status',
            title: 'Status',
            no_search: true,
            values: () => [
                { key: 'active', name: 'Active' },
                { key: 'paused', name: 'Paused' },
                { key: 'cancelled', name: 'Cancelled' },
                { key: 'pastdue', name: 'Past Due' },
                { key: 'pending_cancel', name: 'Pending cancellation' },
                { key: 'pending_pause', name: 'Pending pause' },
                { key: 'none', name: 'None' },
                { key: 'giftcard', name: 'Giftcard' },
                { key: 'free', name: 'Free' },
            ],
        })
    }

    addPlan() {
        this._specs.push({
            key: 'plan',
            title: 'Plan',
            type: 'single',
            no_search: true,
            values: () => [
                { key: 'plusone', name: 'Plus 1' },
                { key: 'standard', name: 'Standard' },
            ],
        })
    }

    addIsTest() {
        this._specs.push({
            key: 'is_test',
            title: 'Is test',
            type: 'single',
            no_search: true,
            values: () => [
                { key: 'true', name: 'Yes' },
                { key: 'false', name: 'No' },
            ],
        })
    }

    addIsAdmin() {
        this._specs.push({
            key: 'is_admin',
            title: 'Is admin',
            type: 'single',
            no_search: true,
            values: () => [
                { key: 'true', name: 'Yes' },
                { key: 'false', name: 'No' },
            ],
        })
    }

    addCountry() {
        this._specs.push({
            key: 'country',
            title: 'Country',
            no_search: true,
            type: 'single',
            values: () => [
                { key: 'SE', name: 'Sweden' },
                { key: 'DE', name: 'Germany' },
            ],
        })
    }

    addCity(login?: boolean) {
        let values = c_cities.all;
        if(login)
            values = values.filter(c => c.can_signup);

        this._specs.push({
            key: 'city',
            title: 'City',
            values: () => values.map(c => ({ key: c.id, name: c.name }))
        });
    }

    addProducer(set?: Set<YId>) {
        let producers = c_producers.all;
        if(set)
            producers = producers.filter(p => set.has(p.id));

        this._specs.push({
            key: 'producer',
            title: 'Producer',
            values: () => producers.map(c => ({ name: c.name, key: c.id }))
        });
    }

    addVenue(set?: Set<YId>) {
        let venues = c_venues.all;
        if(set)
            venues = venues.filter(p => set.has(p.id));

        this._specs.push({
            key: 'venue',
            title: 'Venue',
            values: () => venues.map(c => ({ name: c.name, key: c.id }))
        });
    }

    addGenre() {
        this._specs.push({
            key: 'genre',
            title: 'Genre',
            values: () => c_event_tags.all.filter(t => t.is_genre).map(t => ({ name: cEventTagName(t), key: t.id })),
        });
    }

    addCustom(key: string, title: string, values: () => UIValue<any>[]) {
        this._specs.push({ key, title, values });
    }

    addUnpublished() {
        this._specs.push({
            key: 'unpublished',
            title: 'Unpublished'
        });
    }

    addFromDate() {
        this._specs.push({
            key: 'from_date',
            title: 'From date',
            type: 'date',
        })
    }

    addToDate() {
        this._specs.push({
            key: 'to_date',
            title: 'To date',
            type: 'date',
        })
    }

    private _fixChips() {
        const chips = this.filters.map(f => {
            const close = $el.ICON(UIIcon.XMARK | UIIcon.WEIGHT_SOLID, 'r-close');
            const chip = $el.DIV('ab-filter-chip', [
                $el.DIV('r-top', f.title),
                $el.DIV('r-bottom', f.value_text),
                close
            ]);
            close.addEventListener('click', ev => {
                this.remove(f.key);
                ev.stopPropagation();
            });
            chip.addEventListener('click', () => {
                this.showSubmenu(chip, f.key);
            });
            chip.classList.add('u-clickable');
            return chip;
        });

        this._chip_container.ui_empty(chips);
        this._chip_container.ui_show(undefined, chips.length > 0);
    }

    add(key: string, title: string, value: any, value_text: string) {
        let found = false;
        for(const f of this.filters) {
            if(f.key == key) {
                f.value = value;
                f.value_text = value_text;
                found = true;
                break;
            }
        }

        if(!found) {
            this.filters.push({ key, title, value, value_text });
        }

        this._fixChips();
        this.on_filters?.();
    }

    remove(key: string) {
        const filters = this.filters;
        for(let i = 0; i < filters.length; i++) {
            if(filters[i].key == key) {
                filters.splice(i, 1);
                this._fixChips();
                this.on_filters?.();
                break;
            }
        }
    }

    private _makePopup(spec: TLNewFilterSpec) {
        const current = this.filters.find(fl => fl.key == spec.key);

        if(spec.type == 'date') {
            const picker = new UIDatePicker;
            if(current)
                picker.value = current.value;
            $on(picker, 'value', v => {
                uiHidePopup(picker, true);
                this.add(spec.key, spec.title, v, v.format('yyyy-MM-dd'));
            });
            return picker;
        } else if(spec.values) {
            const popup = new UIFilterPopup(spec.values!(), current?.value, { no_search: spec.no_search, single: spec.type == 'single' });
            popup.on_filter = values => {
                uiHidePopup(popup, true);
                if(values.length) {
                    this.add(spec.key, spec.title, values.map(v => v.key), values.map(v => v.name).join(', '));
                } else {
                    this.remove(spec.key);
                }
            }
            return popup;
        } else {
            throw new Error('Invalid filter spec');
        }
    }

    showSubmenu(where: UIViewSpec, key: string) {
        for(const f of this._specs) {
            if(f.key == key) {
                const popup = this._makePopup(f);
                uiShowPopup(popup, where, { position: UIRelPos.Below | UIRelPos.End, capture_mouse: true });
                break;
            }
        }
    }

    showMenu(where: UIViewSpec) {
        const filter_menu = new UIMenu(this._specs.map(f => {
            if(f.type == 'date' || f.values) {
                return {
                    title: f.title,
                    popup: () => this._makePopup(f)
                }
            }
            return {
                title: f.title,
                key: f.key
            }
        }));

        filter_menu.on_option = i => {
            if(i.key)
                this.add(i.key, i.key, [], yResolve(i.title)!);
        }

        uiShowMenu(filter_menu, where, UIRelPos.Below | UIRelPos.Start);
    }
}
